import type {
  CrmChatProviderKey,
  CrmLeadIntegrationChatTab,
  CrmLeadProviderChatAccount,
  CrmLeadProviderChatChannel,
  CrmLeadProviderChatDialog,
  CrmLeadProviderChatMeta,
} from '@/api/types/crm/crmLeadFunnels.types'

const sumUnread = (items: Array<{ unread_count?: number | null }>): number =>
  items.reduce((sum, item) => {
    const count = Number(item.unread_count ?? 0)
    return sum + (Number.isFinite(count) && count > 0 ? Math.floor(count) : 0)
  }, 0)

const isValidDialog = (
  dialog: CrmLeadProviderChatDialog | null | undefined,
): dialog is CrmLeadProviderChatDialog => {
  const conversationId = Number(dialog?.conversation_id)
  return Number.isFinite(conversationId) && conversationId > 0
}

/** Аккаунты провайдера; legacy `integrations[]` группируется в accounts. */
export const getLeadProviderChatAccounts = (
  meta: CrmLeadProviderChatMeta | null | undefined,
): CrmLeadProviderChatAccount[] => {
  if (!meta) {
    return []
  }

  if (Array.isArray(meta.accounts) && meta.accounts.length > 0) {
    return meta.accounts.filter(
      (account) =>
        account != null &&
        Number.isFinite(Number(account.integration_id)) &&
        Number(account.integration_id) > 0,
    )
  }

  const legacyTabs = meta.integrations
  if (!Array.isArray(legacyTabs) || legacyTabs.length === 0) {
    return []
  }

  const byIntegrationId = new Map<number, CrmLeadProviderChatAccount>()

  legacyTabs.forEach((tab) => {
    const integrationId = Number(tab?.integration_id)
    const conversationId = Number(tab?.conversation_id)
    if (
      !Number.isFinite(integrationId) ||
      integrationId <= 0 ||
      !Number.isFinite(conversationId) ||
      conversationId <= 0
    ) {
      return
    }

    const dialog: CrmLeadProviderChatDialog = {
      conversation_id: conversationId,
      recipient: String(tab.recipient ?? ''),
      title: tab.title ?? tab.name ?? null,
      unread_count: Number(tab.unread_count) || 0,
      last_interaction_at: tab.last_interaction_at ?? null,
      attached: true,
      external_chat_type: tab.external_chat_type ?? null,
      external_profile_url: tab.external_profile_url ?? null,
    }

    const existing = byIntegrationId.get(integrationId)
    if (existing) {
      existing.dialogs = [...(existing.dialogs ?? []), dialog]
      existing.unread_count = sumUnread(existing.dialogs)
      return
    }

    byIntegrationId.set(integrationId, {
      integration_id: integrationId,
      name: tab.name ?? null,
      status: null,
      unread_count: dialog.unread_count,
      dialogs: [dialog],
    })
  })

  return Array.from(byIntegrationId.values())
}

export const isWazzupLikeAccount = (
  account: CrmLeadProviderChatAccount,
): boolean => Array.isArray(account.channels)

export type CrmLeadProviderChatDialogRef = {
  account: CrmLeadProviderChatAccount
  channel: CrmLeadProviderChatChannel | null
  dialog: CrmLeadProviderChatDialog
}

/** Все диалоги провайдера плоско (для таблицы / доступа / дока). */
export const flattenLeadProviderChatDialogs = (
  meta: CrmLeadProviderChatMeta | null | undefined,
): CrmLeadProviderChatDialogRef[] => {
  const accounts = getLeadProviderChatAccounts(meta)
  const result: CrmLeadProviderChatDialogRef[] = []

  accounts.forEach((account) => {
    if (isWazzupLikeAccount(account) && account.channels) {
      account.channels.forEach((channel) => {
        ;(channel.dialogs ?? []).filter(isValidDialog).forEach((dialog) => {
          result.push({ account, channel, dialog })
        })
      })
      return
    }

    ;(account.dialogs ?? []).filter(isValidDialog).forEach((dialog) => {
      result.push({ account, channel: null, dialog })
    })
  })

  return result
}

const buildFlatTab = (
  provider: CrmChatProviderKey,
  ref: CrmLeadProviderChatDialogRef,
): CrmLeadIntegrationChatTab => {
  const { account, channel, dialog } = ref
  // Вкладка = контакт (dialog.title), не «аккаунт · канал · бот».
  // Fallback на account/channel только если title ещё нет (новый чат по ссылке).
  const contactTitle = dialog.title?.trim() || null
  const channelFallback = [account.name?.trim(), channel?.name?.trim()]
    .filter(Boolean)
    .join(' · ')

  return {
    id: account.integration_id,
    integration_id: account.integration_id,
    conversation_id: dialog.conversation_id,
    provider,
    name: contactTitle || channelFallback || null,
    unread_count: Number(dialog.unread_count) || 0,
    last_interaction_at: dialog.last_interaction_at,
    recipient: dialog.recipient,
    attached: true,
    title: dialog.title,
    external_channel_id: channel?.external_channel_id ?? null,
    external_chat_type: dialog.external_chat_type,
    external_profile_url: dialog.external_profile_url,
  }
}

export const flattenLeadProviderChatTabs = (
  meta: CrmLeadProviderChatMeta | null | undefined,
  provider: CrmChatProviderKey,
): CrmLeadIntegrationChatTab[] =>
  flattenLeadProviderChatDialogs(meta).map((ref) => buildFlatTab(provider, ref))

/** Собрать accounts из плоских вкладок (resolve / локальные патчи). */
export const buildLeadProviderAccountsFromTabs = (
  tabs: CrmLeadIntegrationChatTab[],
): CrmLeadProviderChatAccount[] => {
  const byIntegrationId = new Map<number, CrmLeadProviderChatAccount>()

  tabs.forEach((tab) => {
    const integrationId = Number(tab.integration_id)
    const conversationId = Number(tab.conversation_id)
    if (
      !Number.isFinite(integrationId) ||
      integrationId <= 0 ||
      !Number.isFinite(conversationId) ||
      conversationId <= 0
    ) {
      return
    }

    const dialog: CrmLeadProviderChatDialog = {
      conversation_id: conversationId,
      recipient: String(tab.recipient ?? ''),
      title: tab.title ?? null,
      unread_count: Number(tab.unread_count) || 0,
      last_interaction_at: tab.last_interaction_at ?? null,
      attached: true,
      external_chat_type: tab.external_chat_type ?? null,
      external_profile_url: tab.external_profile_url ?? null,
    }

    const channelId = tab.external_channel_id?.trim()
    const existing = byIntegrationId.get(integrationId)

    if (channelId) {
      const channel: CrmLeadProviderChatChannel = {
        external_channel_id: channelId,
        channel_id: null,
        type: tab.external_chat_type ?? 'wazzup',
        name: tab.name ?? null,
        status: null,
        unread_count: dialog.unread_count,
        dialogs: [dialog],
      }

      if (existing) {
        const channels = existing.channels ?? []
        const channelIndex = channels.findIndex(
          (item) => item.external_channel_id === channelId,
        )
        if (channelIndex >= 0) {
          const prev = channels[channelIndex]
          const dialogs = [...prev.dialogs, dialog]
          channels[channelIndex] = {
            ...prev,
            dialogs,
            unread_count: sumUnread(dialogs),
          }
        } else {
          channels.push(channel)
        }
        existing.channels = channels
        existing.unread_count = sumUnread(channels)
        return
      }

      byIntegrationId.set(integrationId, {
        integration_id: integrationId,
        name: tab.name ?? null,
        status: null,
        unread_count: dialog.unread_count,
        channels: [channel],
      })
      return
    }

    if (existing) {
      const dialogs = [...(existing.dialogs ?? []), dialog]
      existing.dialogs = dialogs
      existing.unread_count = sumUnread(dialogs)
      return
    }

    byIntegrationId.set(integrationId, {
      integration_id: integrationId,
      name: tab.name ?? null,
      status: null,
      unread_count: dialog.unread_count,
      dialogs: [dialog],
    })
  })

  return Array.from(byIntegrationId.values())
}

const zeroDialogUnread = (
  dialog: CrmLeadProviderChatDialog,
  conversationId?: number,
): CrmLeadProviderChatDialog => {
  if (
    conversationId != null &&
    Number(dialog.conversation_id) !== Number(conversationId)
  ) {
    return dialog
  }
  return { ...dialog, unread_count: 0 }
}

/** Обнулить unread диалога(ов) внутри accounts и пересчитать суммы. */
export const zeroLeadProviderChatUnreadInMeta = (
  meta: CrmLeadProviderChatMeta,
  conversationId?: number,
): CrmLeadProviderChatMeta => {
  const accounts = getLeadProviderChatAccounts(meta).map((account) => {
    if (isWazzupLikeAccount(account) && account.channels) {
      const channels = account.channels.map((channel) => {
        const dialogs = (channel.dialogs ?? []).map((dialog) =>
          zeroDialogUnread(dialog, conversationId),
        )
        return {
          ...channel,
          dialogs,
          unread_count: sumUnread(dialogs),
        }
      })
      return {
        ...account,
        channels,
        unread_count: sumUnread(channels),
      }
    }

    const dialogs = (account.dialogs ?? []).map((dialog) =>
      zeroDialogUnread(dialog, conversationId),
    )
    return {
      ...account,
      dialogs,
      unread_count: sumUnread(dialogs),
    }
  })

  return {
    ...meta,
    accounts,
    unread_count: sumUnread(accounts),
    has_chat: meta.has_chat || accounts.length > 0,
  }
}
